import * as Schema from "effect/Schema";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

/**
 * Provider-neutral Music domain. This does not replace or rename Spotify
 * protocol v1: provider adapters translate between this contract and their
 * own wire protocols. Display labels are never provider identity.
 */
export const MUSIC_PROTOCOL_VERSION = 1 as const;

export const MusicProviderId = Schema.Literals(["spotify", "appleMusic"]);
export type MusicProviderId = typeof MusicProviderId.Type;

export const MusicItemScope = Schema.Literals(["catalog", "library"]);
export type MusicItemScope = typeof MusicItemScope.Type;

export const MusicItemKind = Schema.Literals([
  "track",
  "album",
  "artist",
  "playlist",
  "likedSongs",
]);
export type MusicItemKind = typeof MusicItemKind.Type;

export const MusicItemRef = Schema.Struct({
  provider: MusicProviderId,
  scope: MusicItemScope,
  kind: MusicItemKind,
  id: TrimmedNonEmptyString,
  // The provider's own playable address when it is not the id (Spotify's
  // "spotify:album:hp2" beside the bare id "hp2"). A context plays by it.
  uri: Schema.optionalKey(TrimmedNonEmptyString),
});
export type MusicItemRef = typeof MusicItemRef.Type;

export const MusicTrack = Schema.Struct({
  ref: MusicItemRef,
  // Apple assigns a different id when a catalog song enters the library.
  catalogId: Schema.optionalKey(TrimmedNonEmptyString),
  title: Schema.String,
  artists: Schema.Array(Schema.String),
  album: Schema.String,
  artUrl: Schema.NullOr(Schema.String),
  durationMs: NonNegativeInt,
  isEpisode: Schema.Boolean,
});
export type MusicTrack = typeof MusicTrack.Type;

/**
 * A search's answer. `scope` says where its tracks came from: "library" when
 * the provider searched the person's own library instead of its catalog
 * (Apple Music, while MusicKit gives this Cleo build no developer token).
 * Absent, they are the provider's catalog.
 */
export const MusicSearchAnswer = Schema.Struct({
  tracks: Schema.Array(MusicTrack),
  scope: Schema.optionalKey(MusicItemScope),
});
export type MusicSearchAnswer = typeof MusicSearchAnswer.Type;

export const MusicCollectionCard = Schema.Struct({
  ref: MusicItemRef,
  name: Schema.String,
  subtitle: Schema.String,
  description: Schema.NullOr(Schema.String),
  artUrl: Schema.NullOr(Schema.String),
  itemCount: Schema.NullOr(NonNegativeInt),
});
export type MusicCollectionCard = typeof MusicCollectionCard.Type;

export const MusicDevice = Schema.Struct({
  id: TrimmedNonEmptyString,
  name: Schema.String,
  kind: Schema.String,
  isActive: Schema.Boolean,
  isLocal: Schema.Boolean,
  volumePercent: NonNegativeInt,
});
export type MusicDevice = typeof MusicDevice.Type;

/** Capabilities are computed from the active adapter; they are never settings. */
export const MusicCapabilities = Schema.Struct({
  search: Schema.Boolean,
  library: Schema.Boolean,
  home: Schema.Boolean,
  queue: Schema.Boolean,
  localPlayback: Schema.Boolean,
  remoteDevices: Schema.Boolean,
  transferPlayback: Schema.Boolean,
  shuffle: Schema.Boolean,
  repeat: Schema.Boolean,
  playlistWrite: Schema.Boolean,
});
export type MusicCapabilities = typeof MusicCapabilities.Type;

export const EMPTY_MUSIC_CAPABILITIES: MusicCapabilities = {
  search: false,
  library: false,
  home: false,
  queue: false,
  localPlayback: false,
  remoteDevices: false,
  transferPlayback: false,
  shuffle: false,
  repeat: false,
  playlistWrite: false,
};

export const MusicAuthorizationStatus = Schema.Literals([
  "signedOut",
  "waitingForUser",
  "connecting",
  "connected",
  "denied",
  "restricted",
  "subscriptionRequired",
  "failed",
]);
export type MusicAuthorizationStatus = typeof MusicAuthorizationStatus.Type;

export const MusicAuthorizationView = Schema.Struct({
  status: MusicAuthorizationStatus,
  accountName: Schema.NullOr(Schema.String),
  actionUrl: Schema.NullOr(Schema.String),
  message: Schema.NullOr(Schema.String),
});
export type MusicAuthorizationView = typeof MusicAuthorizationView.Type;

export const MusicPlayback = Schema.Literals(["stopped", "loading", "playing", "paused"]);
export type MusicPlayback = typeof MusicPlayback.Type;

export const MusicRepeat = Schema.Literals(["off", "context", "track"]);
export type MusicRepeat = typeof MusicRepeat.Type;

export const MusicSnapshot = Schema.Struct({
  sequence: NonNegativeInt,
  playback: MusicPlayback,
  track: Schema.NullOr(MusicTrack),
  positionMs: NonNegativeInt,
  positionAtUnixMs: Schema.NullOr(NonNegativeInt),
  volumePercent: NonNegativeInt,
  shuffle: Schema.Boolean,
  repeat: MusicRepeat,
  activeDeviceId: Schema.NullOr(Schema.String),
  activeDeviceName: Schema.NullOr(Schema.String),
  isLocalDevice: Schema.Boolean,
});
export type MusicSnapshot = typeof MusicSnapshot.Type;

export const MusicEngineStatus = Schema.Literals([
  "unchosen",
  "starting",
  "healthy",
  "degraded",
  "unavailable",
  "stopped",
]);
export type MusicEngineStatus = typeof MusicEngineStatus.Type;

export const MusicEngineView = Schema.Struct({
  provider: Schema.NullOr(MusicProviderId),
  status: MusicEngineStatus,
  authorization: Schema.NullOr(MusicAuthorizationView),
  capabilities: MusicCapabilities,
  snapshot: Schema.NullOr(MusicSnapshot),
  lastError: Schema.NullOr(Schema.String),
  /**
   * The provider's code for the last refused command (Spotify's
   * "no-active-device" and "not-started" offer "Play here"); null when nothing was refused.
   */
  lastErrorCode: Schema.NullOr(Schema.String),
  /** Counts refused commands, so a repeated identical refusal shows again. */
  lastErrorSequence: NonNegativeInt,
});
export type MusicEngineView = typeof MusicEngineView.Type;

const version = Schema.Literal(MUSIC_PROTOCOL_VERSION);
export const MusicConnectCommand = Schema.Struct({ version, type: Schema.Literal("connect") });
/**
 * Set up playback on this device. Apple Music: MusicKit's own player here.
 * Spotify: "Play here" — the Spotify app on this Mac takes playback.
 */
export const MusicSetupLocalPlaybackCommand = Schema.Struct({
  version,
  type: Schema.Literal("setupLocalPlayback"),
});
export const MusicToggleCommand = Schema.Struct({ version, type: Schema.Literal("toggle") });
export const MusicPlayCommand = Schema.Struct({ version, type: Schema.Literal("play") });
export const MusicPauseCommand = Schema.Struct({ version, type: Schema.Literal("pause") });
export const MusicNextCommand = Schema.Struct({ version, type: Schema.Literal("next") });
export const MusicPreviousCommand = Schema.Struct({ version, type: Schema.Literal("previous") });
export const MusicSeekCommand = Schema.Struct({
  version,
  type: Schema.Literal("seek"),
  positionMs: NonNegativeInt,
});
export const MusicVolumeCommand = Schema.Struct({
  version,
  type: Schema.Literal("volume"),
  percent: NonNegativeInt,
});
export const MusicShuffleCommand = Schema.Struct({
  version,
  type: Schema.Literal("shuffle"),
  enabled: Schema.Boolean,
});
export const MusicRepeatCommand = Schema.Struct({
  version,
  type: Schema.Literal("repeat"),
  mode: MusicRepeat,
});
export const MusicTransferCommand = Schema.Struct({
  version,
  type: Schema.Literal("transfer"),
  deviceId: TrimmedNonEmptyString,
  play: Schema.Boolean,
});
export const MusicLoadCommand = Schema.Struct({
  version,
  type: Schema.Literal("load"),
  context: Schema.optionalKey(MusicItemRef),
  items: Schema.optionalKey(Schema.Array(MusicItemRef)),
  offset: Schema.optionalKey(MusicItemRef),
  play: Schema.Boolean,
});
export const MusicAddToQueueCommand = Schema.Struct({
  version,
  type: Schema.Literal("addToQueue"),
  item: MusicItemRef,
});

export const MusicUserCommand = Schema.Union([
  MusicConnectCommand,
  MusicSetupLocalPlaybackCommand,
  MusicToggleCommand,
  MusicPlayCommand,
  MusicPauseCommand,
  MusicNextCommand,
  MusicPreviousCommand,
  MusicSeekCommand,
  MusicVolumeCommand,
  MusicShuffleCommand,
  MusicRepeatCommand,
  MusicTransferCommand,
  MusicLoadCommand,
  MusicAddToQueueCommand,
]);
export type MusicUserCommand = typeof MusicUserCommand.Type;

/**
 * A read of the person's library, home or devices names the provider it is
 * for. The client keeps each provider's answer under its own key, and the
 * server refuses a read for a provider that is not the selected one
 * ("provider-mismatch"), so an answer from the middle of a switch never lands
 * under the other provider. Optional only so an older client's read still
 * answers; every current caller names it.
 */
export const MusicReadInput = Schema.Struct({
  provider: Schema.optionalKey(MusicProviderId),
});
export type MusicReadInput = typeof MusicReadInput.Type;

export const MusicLibrary = Schema.Struct({
  playlists: Schema.Array(MusicCollectionCard),
  albums: Schema.Array(MusicCollectionCard),
  artists: Schema.Array(MusicCollectionCard),
  likedSongs: Schema.NullOr(MusicCollectionCard),
  /**
   * When the provider stopped a list before its end (a bound on size or
   * time), how many are shown, in the person's words. Absent when every list
   * is whole.
   */
  notice: Schema.optionalKey(Schema.String),
});
export type MusicLibrary = typeof MusicLibrary.Type;

export const MusicHome = Schema.Struct({
  pinned: Schema.Array(MusicCollectionCard),
  madeForYou: Schema.Array(MusicCollectionCard),
  recentlyPlayed: Schema.Array(MusicTrack),
  topArtists: Schema.Array(MusicCollectionCard),
  topTracks: Schema.Array(MusicTrack),
  recommendations: Schema.Array(MusicTrack),
});
export type MusicHome = typeof MusicHome.Type;

export const MusicCollection = Schema.Struct({
  ref: MusicItemRef,
  name: Schema.String,
  owner: Schema.String,
  description: Schema.NullOr(Schema.String),
  artUrl: Schema.NullOr(Schema.String),
  items: Schema.Array(MusicTrack),
});
export type MusicCollection = typeof MusicCollection.Type;

export const MusicCreatePlaylistInput = Schema.Struct({
  requestId: Schema.String,
  name: Schema.String,
  description: Schema.String,
  items: Schema.Array(MusicItemRef),
});
export type MusicCreatePlaylistInput = typeof MusicCreatePlaylistInput.Type;

export const MusicErrorCode = Schema.Literals([
  "provider-unchosen",
  "provider-unavailable",
  "provider-mismatch",
  "authorization-required",
  "subscription-required",
  "unsupported-command",
  "provider-failure",
]);
export type MusicErrorCode = typeof MusicErrorCode.Type;

export class MusicEngineCallError extends Schema.TaggedErrorClass<MusicEngineCallError>()(
  "MusicEngineCallError",
  {
    code: MusicErrorCode,
    provider: Schema.NullOr(MusicProviderId),
    reason: Schema.String,
  },
) {
  override get message(): string {
    return this.reason;
  }
}

/** Footer display position: interpolate only while the selected provider plays. */
export function displayedMusicPositionMs(snapshot: MusicSnapshot, nowUnixMs: number): number {
  const duration = snapshot.track?.durationMs ?? Number.POSITIVE_INFINITY;
  if (snapshot.playback !== "playing" || snapshot.positionAtUnixMs === null) {
    return Math.min(snapshot.positionMs, duration);
  }
  return Math.min(
    snapshot.positionMs + Math.max(0, nowUnixMs - snapshot.positionAtUnixMs),
    duration,
  );
}
